/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable complexity */

'use client'

import { useMemo, useState } from 'react'
import {
  Stack,
  Typography,
  Button,
  TextField,
  Divider,
  Box,
} from '@mui/material'
import { Link } from '@assets-components'
import { Icon } from '@media-components'
import { Accordion } from '@components/common'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

/**
 * Props for the CategoryFilterSection component
 * Used to render the category filter chips in the aside section
 */
export type CategoryItem = string | { id: string; name: string; slug: string }

export type CategoryFilterSectionProps = {
  /** Array of category names to display as filter chips */
  categoryData: CategoryItem[]
  /** Title for the category filter section (e.g., "Category", "Topics") */
  categoryTitle: string

  /**
   * Callback when selected category changes
   * @param category - The selected category, or undefined if "All" is selected
   */
  onChange?: (category: string | undefined) => void

  /** Base URL path for category links (e.g., "/blog/category") - if provided, uses links instead of buttons */
  categoryLinkBasePath?: string

  /** Custom styles */
  customStyles?: CustomStyles<StylesClassNames>

  /** Default selected category (undefined = All) */
  defaultSelected?: string

  // ============================================
  // "ALL" OPTION CONFIGURATION
  // ============================================

  /** Whether to show an "All" button at the start (default: false) */
  withAllOption?: boolean

  /** Label for the "All" button (default: "All") */
  allLabel?: string

  // ============================================
  // CONTROLLED MODE (optional - for advanced use cases)
  // ============================================

  /**
   * Controlled selected value - when provided, component becomes controlled
   * Use this only when you need external control over selection
   */
  selectedFilter?: string | string[]

  /** Function to check if a filter is selected (for controlled mode) */
  isFilterSelected?: (filter: string) => boolean

  /** Callback when a category chip is clicked (for controlled mode) */
  onCategoryClick?: (category: string) => void

  /** Callback when "All" button is clicked (for controlled mode) */
  onAllClick?: () => void

  /** Whether "All" is currently selected (for controlled mode) */
  isAllSelected?: boolean

  /** Whether to enable multi-select mode (for controlled mode) */
  multiSelect?: boolean

  /** Whether to disable selected state (for controlled mode) */
  disableSelectedState?: boolean

  // ============================================
  // MOBILE ACTIONS (Clear All + Apply Filters)
  // ============================================

  /** Callback when "Apply Filters" is clicked (mobile only) */
  onApplyFilter?: () => void

  /** Callback when "Clear All" is clicked (mobile only) */
  onClearFilter?: () => void

  /** Label for Apply Filters button (default: "Apply Filters") */
  applyFilterLabel?: string

  /** Label for Clear All button (default: "Clear All") */
  clearAllLabel?: string

  // ============================================
  // MOBILE CATEGORY SEARCH
  // ============================================

  /** Whether to show a search input for filtering categories (mobile only, default: false) */
  showCategorySearch?: boolean

  /** Placeholder for category search input (default: "Search by...") */
  categorySearchPlaceholder?: string

  /** Title for not-found state (default: "No category exists") */
  notFoundTitle?: string

  /** Label for the clear CTA in not-found state (default: "Clear Filter") */
  notFoundClearLabel?: string

  /** Current category */
  currentCategory?: string
}

/**
 * Renders the category filter section with accordion
 * Can be used standalone or as part of PaginationSearchSection aside
 */
export function CategoryFilterSection({
  categoryData,
  categoryTitle,
  onChange,
  categoryLinkBasePath,
  customStyles,
  defaultSelected,
  // All option
  withAllOption = false,
  allLabel = 'All',
  // Controlled mode props
  selectedFilter,
  isFilterSelected: externalIsFilterSelected,
  onCategoryClick,
  onAllClick,
  isAllSelected: externalIsAllSelected,
  multiSelect = false,
  disableSelectedState = false,
  // Mobile actions
  onApplyFilter,
  onClearFilter,
  applyFilterLabel = 'Apply Filters',
  clearAllLabel = 'Clear All',
  // Mobile category search
  showCategorySearch = false,
  categorySearchPlaceholder = 'Search by...',
  notFoundTitle = 'No category exists',
  notFoundClearLabel = 'Clear Filter',
  currentCategory,
}: CategoryFilterSectionProps) {
  const styles = getStyles(defaultStyles, customStyles)

  // Internal state for uncontrolled mode
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    defaultSelected
  )

  // Category search state (mobile only)
  const [categorySearch, setCategorySearch] = useState('')

  const getCategoryName = (category: CategoryItem) =>
    typeof category === 'string' ? category : category.name
  const getCategorySlug = (category: CategoryItem) =>
    typeof category === 'string' ? category : category.slug
  const getCategoryId = (category: CategoryItem) =>
    typeof category === 'string' ? category : category.id

  // Determine if we're in controlled or uncontrolled mode
  const isControlled =
    selectedFilter !== undefined || onCategoryClick !== undefined

  // Get the current selected value
  const currentSelected = isControlled ? selectedFilter : internalSelected

  // Handle category selection
  const handleCategoryClick = (category: string) => {
    if (isControlled) {
      // Controlled mode - call external handler
      onCategoryClick?.(category)
    } else {
      // Uncontrolled mode - manage internal state
      setInternalSelected(category)
      onChange?.(category)
    }
  }

  // Handle "All" click
  const handleAllClick = () => {
    if (isControlled) {
      // Controlled mode - call external handler
      onAllClick?.()
    } else {
      // Uncontrolled mode - manage internal state
      setInternalSelected(undefined)
      onChange?.(undefined)
    }
  }

  // Check if a filter is selected
  const isFilterSelected = (category: string): boolean => {
    if (externalIsFilterSelected) {
      return externalIsFilterSelected(category)
    }
    if (multiSelect && Array.isArray(currentSelected)) {
      return currentSelected.includes(category)
    }
    return currentSelected === category
  }

  // Check if "All" is selected
  const isAllSelected = (): boolean => {
    if (externalIsAllSelected !== undefined) {
      return externalIsAllSelected
    }
    return (
      !currentSelected ||
      (Array.isArray(currentSelected) && currentSelected.length === 0) ||
      currentSelected === ''
    )
  }

  // Filter categories based on search
  const filteredCategoryData = useMemo(() => {
    if (!categorySearch.trim()) return categoryData
    return (
      categoryData.filter((category) =>
        getCategoryName(category)
          .toLowerCase()
          .includes(categorySearch.toLowerCase())
      ) ?? []
    )
  }, [categoryData, categorySearch])

  const hasMobileActions = onApplyFilter || onClearFilter

  const categoryFilterContent = (
    <>
      {/* "All" option button */}
      {withAllOption && (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            handleAllClick()
          }}
          variant="outlined"
          sx={{
            fontWeight: 500,
            height: '32px !important',
          }}
          selected={isAllSelected()}
        >
          <Typography variant="caption1.500">{allLabel}</Typography>
        </Button>
      )}

      {/* Category buttons */}
      {filteredCategoryData?.map((category, key) => {
        const cSlug = getCategorySlug(category)
        const cName = getCategoryName(category)
        const selected = isFilterSelected(cSlug)

        // If using links, render as link
        const chipProps = categoryLinkBasePath
          ? {
              component: Link,
              href: `${categoryLinkBasePath}/${cSlug}`,
            }
          : {}

        return (
          <Button
            key={getCategoryId(category) || key}
            onClick={(e) => {
              e.stopPropagation()
              if (showCategorySearch) {
                handleCategoryClick(cSlug)
              }
            }}
            variant="outlined"
            sx={{
              fontWeight: 500,
              height: '32px !important',
            }}
            // for mobile we select category only when apply filter is clicked
            selected={!disableSelectedState && selected}
            {...chipProps}
          >
            <Typography variant="caption1.500">{cName}</Typography>
          </Button>
        )
      })}
    </>
  )

  return (
    <Stack {...styles('filterWrapper')}>
      {/* Mobile Category Search */}
      {showCategorySearch && (
        <Box
          sx={{
            display: 'none',
            mb: 1,
          }}
        >
          <TextField
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            placeholder={categorySearchPlaceholder}
            fullWidth
            InputProps={{
              startAdornment: (
                <Icon
                  iconName="AdvanceSearch"
                  fontSize="medium"
                  sx={{ marginTop: '0.5rem' }}
                />
              ),
              ...(categorySearch.length > 0 && {
                endAdornment: (
                  <Icon
                    iconName="Cross"
                    fontSize="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setCategorySearch('')}
                  />
                ),
              }),
            }}
          />
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      {/* Category Accordion */}
      {filteredCategoryData?.length > 0 ? (
        <>
          <Accordion
            accordionData={[
              { title: categoryTitle, children: categoryFilterContent },
            ]}
            openInitial
            needBackground={false}
            csx={{
              accordionWrapper: styles('accordionWrapper').sx,
              drawer: styles('accordionDrawer').sx,
              wrapper: {
                display: {
                  xs: 'none',
                  sm: 'flex',
                },
              },
            }}
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            gap="0.5rem"
            display={{
              xs: 'flex',
              sm: 'none',
            }}
          >
            {categoryFilterContent}
          </Stack>
        </>
      ) : (
        /* Not Found State (mobile category search yielded no results) */
        <Box {...styles('categoryNotFoundWrapper')}>
          <Typography
            variant="body1.600"
            color="text.secondary"
            textAlign="center"
          >
            {notFoundTitle}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setCategorySearch('')}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
            }}
          >
            <Typography variant="body2.500">{notFoundClearLabel}</Typography>
          </Button>
        </Box>
      )}

      {/* Mobile Actions Footer (Clear All + Apply Filters) */}
      {hasMobileActions &&
        filteredCategoryData.length > 0 &&
        showCategorySearch && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
            }}
          >
            <Divider sx={{ my: 2 }} />
            <Box {...styles('ctaWrapper')}>
              {onClearFilter && (
                <Typography
                  variant="h6.600"
                  color="text.primary"
                  onClick={onClearFilter}
                  sx={{ cursor: 'pointer' }}
                >
                  {clearAllLabel}
                </Typography>
              )}
              {onApplyFilter && (
                <Button
                  variant="contained"
                  onClick={onApplyFilter}
                  {...styles('applyFilterButton')}
                  // disable if user on same category page
                  disabled={
                    currentCategory
                      ? isFilterSelected(currentCategory)
                      : isAllSelected()
                  }
                >
                  <Typography variant="body1.600" color="common.white">
                    {applyFilterLabel}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
        )}
    </Stack>
  )
}
